import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { usersApi } from '../../api/users';
import type { UserRole } from '../../types/index.js';

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Admin',
  city_admin: 'Stadt-Admin',
  member: 'Mitglied',
  read_only: 'Lesezugriff',
};

type InvitationFilter = 'all' | 'active' | 'old';

function getInvitationStatus(invitation: {
  accepted_at: string | null;
  revoked_at: string | null;
  expires_at: string;
}): { key: InvitationFilter | 'accepted' | 'expired' | 'revoked'; label: string; className: string } {
  if (invitation.accepted_at) {
    return { key: 'accepted', label: 'Angenommen', className: 'bg-green-100 text-green-800' };
  }
  if (invitation.revoked_at) {
    return { key: 'revoked', label: 'Widerrufen', className: 'bg-gray-200 text-gray-800' };
  }
  const isExpired = new Date(invitation.expires_at).getTime() <= Date.now();
  if (isExpired) {
    return { key: 'expired', label: 'Abgelaufen', className: 'bg-amber-100 text-amber-800' };
  }
  return { key: 'active', label: 'Aktiv', className: 'bg-blue-100 text-blue-800' };
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('de-DE');
}

export default function UserInvitations() {
  const [filter, setFilter] = useState<InvitationFilter>('all');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-invitations'],
    queryFn: usersApi.listInvitations,
  });

  const invalidateMutation = useMutation({
    mutationFn: (invitationId: number) => usersApi.invalidateInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-invitations'] });
    },
  });

  const invitations = data?.items ?? [];

  const counts = useMemo(() => {
    let active = 0;
    let old = 0;
    for (const invitation of invitations) {
      const status = getInvitationStatus(invitation);
      if (status.key === 'active') {
        active += 1;
      } else {
        old += 1;
      }
    }
    return { all: invitations.length, active, old };
  }, [invitations]);

  const filtered = useMemo(() => {
    if (filter === 'all') return invitations;
    return invitations.filter((invitation) => {
      const status = getInvitationStatus(invitation);
      if (filter === 'active') {
        return status.key === 'active';
      }
      return status.key !== 'active';
    });
  }, [filter, invitations]);

  if (isLoading) return <div className="p-6">Laedt...</div>;
  if (error) return <div className="p-6 text-red-600">Fehler: {(error as Error).message}</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Einladungen</h1>
        <Link
          to="/settings/users"
          className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
        >
          Zur Nutzerverwaltung
        </Link>
      </div>

      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded border ${filter === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          Alle ({counts.all})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-3 py-1.5 rounded border ${filter === 'active' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          Aktiv ({counts.active})
        </button>
        <button
          onClick={() => setFilter('old')}
          className={`px-3 py-1.5 rounded border ${filter === 'old' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          Historisch ({counts.old})
        </button>
      </div>

      {invalidateMutation.error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(invalidateMutation.error as Error).message}
        </div>
      )}

      <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">E-Mail</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Rolle</th>
              <th className="px-4 py-2 text-left">Stadt</th>
              <th className="px-4 py-2 text-left">Erstellt</th>
              <th className="px-4 py-2 text-left">Ablauf</th>
              <th className="px-4 py-2 text-left">Abschluss</th>
              <th className="px-4 py-2 text-left">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                  Keine Einladungen gefunden.
                </td>
              </tr>
            )}
            {filtered.map((invitation) => {
              const status = getInvitationStatus(invitation);
              return (
                <tr key={invitation.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-2">{invitation.email}</td>
                  <td className="px-4 py-2">{invitation.full_name}</td>
                  <td className="px-4 py-2">{ROLE_LABELS[invitation.role]}</td>
                  <td className="px-4 py-2 text-gray-500">{invitation.city_id ?? '-'}</td>
                  <td className="px-4 py-2">{formatDateTime(invitation.created_at)}</td>
                  <td className="px-4 py-2">{formatDateTime(invitation.expires_at)}</td>
                  <td className="px-4 py-2">
                    {invitation.accepted_at ? formatDateTime(invitation.accepted_at) : invitation.revoked_at ? formatDateTime(invitation.revoked_at) : '-'}
                  </td>
                  <td className="px-4 py-2">
                    {status.key === 'active' ? (
                      <button
                        onClick={() => {
                          if (!window.confirm('Einladung wirklich ungueltig machen?')) return;
                          invalidateMutation.mutate(invitation.id);
                        }}
                        disabled={invalidateMutation.isPending}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Ungueltig machen
                      </button>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
